import type { XkEvent } from '@/lib/api/types';

/**
 * Lifecycle phase of a single event, derived from its dates + status. Shared by
 * the Planner (PLAN tab — only editable events are draggable targets) and the
 * TRACK tab (groups events by phase).
 *
 *   live     — Active and now ∈ [start, end]
 *   upcoming — Active, scheduled, and now < start
 *   edit     — still being configured: missing dates, or Disabled draft
 *   done     — Finished, or now > end
 */
export type EventPhase = 'live' | 'upcoming' | 'edit' | 'done';

export function eventPhase(e: XkEvent, now: number = Date.now()): EventPhase {
  const start = e.startDate ? new Date(e.startDate).getTime() : null;
  const end = e.endDate ? new Date(e.endDate).getTime() : null;

  if (e.eventStatus === 'Finished') return 'done';
  if (end != null && now > end) return 'done';

  if (e.eventStatus === 'Active' && start != null && end != null) {
    if (now >= start && now <= end) return 'live';
    if (now < start) return 'upcoming';
  }
  return 'edit';
}

/**
 * The PLAN tab lists events that are still being planned — not live, not
 * completed. That is everything in the `upcoming` or `edit` phases.
 */
export function isPlannable(e: XkEvent, now: number = Date.now()): boolean {
  const phase = eventPhase(e, now);
  return phase === 'upcoming' || phase === 'edit';
}
